import React, { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Delta } from "@/components/common";
import { TableRow, TableCell } from "@/components/ui/table";
import { toast } from "sonner";
import { fmtUSD, fmtNum } from "@/lib/format";
import { ChevronRight, Scale, Loader2 } from "lucide-react";

const HoldingsPanel = ({ row }) => (
  <div className="rounded-xl border bg-secondary/40 p-3" data-testid={`live-holdings-${row.portfolio_id}`}>
    <div className="grid grid-cols-12 gap-2 px-2 pb-2 text-[10px] uppercase tracking-wide text-muted-foreground">
      <div className="col-span-4">Holding</div>
      <div className="col-span-2 text-end">Price</div>
      <div className="col-span-2 text-end">Value</div>
      <div className="col-span-2 text-end">Today</div>
      <div className="col-span-2 text-end">Weight vs target</div>
    </div>
    <div className="space-y-1">
      {row.holdings.map((h, hi) => (
        <div key={`${h.symbol}-${hi}`} className="grid grid-cols-12 gap-2 items-center rounded-lg bg-background px-2 py-2 text-sm"
          data-testid={`live-holding-${row.portfolio_id}-${h.symbol}`}>
          <div className="col-span-4 min-w-0">
            <div className="font-medium">{h.symbol}</div>
            <div className="text-xs text-muted-foreground truncate">{h.name}</div>
          </div>
          <div className="col-span-2 text-end numeric text-xs">{fmtNum(h.price)}</div>
          <div className="col-span-2 text-end numeric">{fmtUSD(h.value_usd)}</div>
          <div className="col-span-2 flex justify-end"><Delta value={h.day_change_pct} /></div>
          <div className="col-span-2 text-end">
            <span className="numeric text-xs">{h.weight_pct}% / {h.target_weight_pct}%</span>
            <div className={`text-[10px] ${Math.abs(h.drift_pct) > 5 ? "text-amber-600" : "text-muted-foreground"}`}>
              {h.drift_pct > 0 ? "+" : ""}{h.drift_pct}% drift
            </div>
          </div>
        </div>
      ))}
    </div>
  </div>
);

export const LiveRow = ({ row, onRefresh }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const rebalance = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/invest/rebalance/${row.portfolio_id}`);
      const top = data.event.changes[0];
      toast.success("Portfolio rebalanced", {
        description: `${fmtUSD(data.event.moved_usd)} shifted back to target — ${top.symbol} led the drift at ${top.drift_pct}%`,
      });
      await onRefresh();
    } catch (e) { toast.error("Could not rebalance", { description: e?.response?.data?.detail }); }
    finally { setBusy(false); }
  };

  const toggleAuto = async (v) => {
    try {
      await api.patch(`/invest/rebalance/${row.portfolio_id}/settings`, { auto_rebalance: v });
      toast.success(v ? "Auto-rebalance on" : "Auto-rebalance off",
        { description: v ? "We'll quietly realign this portfolio when it drifts." : "You'll rebalance this one manually." });
      await onRefresh();
    } catch (e) { toast.error("Could not update auto-rebalance"); }
  };

  return (
    <>
      <TableRow className="cursor-pointer" onClick={() => setOpen((v) => !v)} data-testid={`live-row-${row.portfolio_id}`}>
        <TableCell>
          <div className="flex items-center gap-2">
            <ChevronRight className={`h-4 w-4 text-muted-foreground transition-transform ${open ? "rotate-90" : ""}`} />
            <div className="min-w-0">
              <div className="font-medium text-sm flex items-center gap-2">
                <span className="truncate">{row.name}</span>
                {row.kind === "halal" && <Badge variant="secondary" className="text-[10px]">Halal</Badge>}
              </div>
              <div className="text-xs text-muted-foreground">{row.holdings_count} holdings</div>
            </div>
          </div>
        </TableCell>
        <TableCell className="text-end numeric">{fmtUSD(row.value_usd)}</TableCell>
        <TableCell className="text-end"><div className="flex justify-end"><Delta value={row.day_change_pct} /></div></TableCell>
        <TableCell className="text-end">
          <div className="numeric text-sm">{fmtUSD(row.pnl_usd)}</div>
          <div className="flex justify-end"><Delta value={row.pnl_pct} /></div>
        </TableCell>
        <TableCell className="text-end">
          <Badge variant={row.needs_rebalance ? "default" : "outline"} className="numeric text-[10px]"
            data-testid={`live-drift-${row.portfolio_id}`}>
            {row.max_drift_pct}%
          </Badge>
        </TableCell>
        <TableCell className="text-end text-xs text-muted-foreground">
          {row.plan ? `${fmtUSD(row.plan.amount_usd)} · ${row.plan.active ? row.plan.next_run : "paused"}` : "—"}
        </TableCell>
        <TableCell className="text-end" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center justify-end gap-2">
            <Switch checked={row.auto_rebalance} onCheckedChange={toggleAuto}
              data-testid={`live-auto-rebalance-${row.portfolio_id}`} />
            <Button size="sm" variant={row.needs_rebalance ? "default" : "secondary"} className="gap-1.5" disabled={busy}
              onClick={rebalance} data-testid={`live-rebalance-${row.portfolio_id}`}>
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Scale className="h-3.5 w-3.5" />}
              Rebalance
            </Button>
          </div>
        </TableCell>
      </TableRow>
      {open && (
        <TableRow>
          <TableCell colSpan={7} className="p-3"><HoldingsPanel row={row} /></TableCell>
        </TableRow>
      )}
    </>
  );
};
